import { GridApi, IGetRowsParams } from 'ag-grid-community';
import { lastValueFrom, Subscription } from 'rxjs';
import { AgGridMultiSelectTracker } from '../../Compliance/AgGrid/MultiSelectTracker';
import { TaxRateCommandCenterService } from './tax.rate.command.center.service';
import { AgGridDataSourceBase, AgGridDataSourceResult } from '../../Compliance/AgGrid';
import { CommandCenterSearchModel } from '../tax.rate.model';
import { EntityType } from '../../constants.new';

export interface TaxRateDataSourceParams {
    entityType: number;
    stateId: number;
    assessorId: number;
    collectorId: number;
    onlyPendingQC: boolean;
    taxYearBegin: number;
    taxYearEnd: number;
}

export interface SelectedRowInfo {
    entityId: number;
    queueProcessingStatus: Core.QueueUpdateStatusEnum;
}

export class TaxRateCommandCenterAgGridDataSource extends AgGridDataSourceBase<TaxRateDataSourceParams> {
    constructor(
        private _gridApi: GridApi,
        private _gridTracker: AgGridMultiSelectTracker,
        private _taxRateCommandCenterService: TaxRateCommandCenterService,
        protected _dataSourceParamsFn: () => TaxRateDataSourceParams
    ) {
        super(_gridApi);
    }

    // All of the xxxx_1 names are from switching to Tax Rate Areas. Ag Grid considers these
    // to be separate fields, even though they're named the same. Probably a good thing, but
    // this is kind of hacky
    private readonly _propertyMap: Compliance.NameValuePair<Core.TaxRatePropertyEnum>[] = [
        { name: 'name', value: Core.TaxRatePropertyEnum.Name },
        { name: 'name_1', value: Core.TaxRatePropertyEnum.Name },
        { name: 'code', value: Core.TaxRatePropertyEnum.Code },
        { name: 'code_1', value: Core.TaxRatePropertyEnum.Code },
        { name: 'disabled', value: Core.TaxRatePropertyEnum.Disabled },
        { name: 'disabled_1', value: Core.TaxRatePropertyEnum.Disabled },
        { name: 'address', value: Core.TaxRatePropertyEnum.Address },
        { name: 'address_1', value: Core.TaxRatePropertyEnum.Address },
        { name: 'assessorName', value: Core.TaxRatePropertyEnum.AssessorName },
        { name: 'assessorName_1', value: Core.TaxRatePropertyEnum.AssessorName },
        { name: 'collectorName', value: Core.TaxRatePropertyEnum.CollectorName },
        { name: 'collectorName_1', value: Core.TaxRatePropertyEnum.CollectorName },
        { name: 'hasComments', value: Core.TaxRatePropertyEnum.HasComments },
        { name: 'hasAttachments', value: Core.TaxRatePropertyEnum.HasAttachments },
        { name: 'ptxId', value: Core.TaxRatePropertyEnum.PtxId },
        { name: 'categoryName', value: Core.TaxRatePropertyEnum.TaxAuthorityCategory },
        { name: 'taxableAssessmentType', value: Core.TaxRatePropertyEnum.TaxableAssessmentType },
        { name: 'obtainInAdvance', value: Core.TaxRatePropertyEnum.ObtainInAdvance },
        { name: 'defaultAnnualRateIncrease', value: Core.TaxRatePropertyEnum.DefaultAnnualRateIncrease },
        { name: 'certificationDate', value: Core.TaxRatePropertyEnum.CertificationDate },
        { name: 'qcRequestTimeUtc', value: Core.TaxRatePropertyEnum.QcRequestTimeUtc },
        { name: 'qcRequestUserFullName', value: Core.TaxRatePropertyEnum.QcRequestUserFullName },
        { name: 'queueProcessingStatusName', value: Core.TaxRatePropertyEnum.QueueProcessingStatusName },
        { name: 'taxAuthorityId', value: Core.TaxRatePropertyEnum.TaxAuthorityId },
        { name: 'taxAuthorityExemptionType', value: Core.TaxRatePropertyEnum.TaxAuthorityExemptionType }
    ];

    private _canceler: () => void;

    getSearchModelWithoutPagination() {
        const searchParams: any = {
            columnFilters: this.getColumnFilters(this._propertyMap),
            sortColumns: this.getSortColumns(this._propertyMap),
            entityType: this._dataSourceParams.entityType,
            stateId: this._dataSourceParams.stateId || null,
            assessorId: this._dataSourceParams.assessorId || null,
            collectorId: this._dataSourceParams.collectorId || null,
            yearBegin: this._dataSourceParams.taxYearBegin,
            yearEnd: this._dataSourceParams.taxYearEnd,
            onlyPendingQC: this._dataSourceParams.onlyPendingQC
        };

        return searchParams;
    }

    getSearchModelForBulkUpdate(skip: number, take: number): Core.TaxRateSearchModel {
        return {
            pagination: {
                skip,
                take: take - skip + 1,
                noPagination: !take
            },
            columnFilters: this.getColumnFilters(this._propertyMap),
            sortColumns: this.getSortColumns(this._propertyMap),
            entityType: Core.EntityTypes.TaxAuthority,
            stateId: this._dataSourceParams.stateId || null,
            assessorId: this._dataSourceParams.assessorId || null,
            collectorId: this._dataSourceParams.collectorId || null,
            onlyPendingQC: this._dataSourceParams.onlyPendingQC,
            exportToExcelMode: false,
            includeOnlyTotals: false,
            includeTotals: false,
            selectedRows: this._gridTracker.getSelectedRowsModel(),
            yearBegin: 0,
            yearEnd: 0
        };
    }

    async getRowIdsInternal(skip: number, take: number): Promise<SelectedRowInfo[]> {
        const selectionOptions = this.getSearchModelForBulkUpdate(skip, take);
        const selectionInfo = await this._taxRateCommandCenterService.getTaxAuthoritySelections(selectionOptions);

        return selectionInfo.map(x => ({
            entityId: x.taxAuthorityId,
            queueProcessingStatus: x.queueProcessingStatus
        }));
    }

    protected canGetRows(): boolean {
        return this._dataSourceParams.stateId !== undefined;
    }

    cancelRequest(): void {
        if (this._canceler) {
            this._canceler();
            this._canceler = null;
        }
    }

    protected async getRowsInternal(params: IGetRowsParams): Promise<any> {
        console.log('getRowsInternal', params);
        const searchParams: CommandCenterSearchModel = {
            pagination: {
                skip: params.startRow,
                take: params.endRow - params.startRow
            },
            columnFilters: this.getColumnFilters(this._propertyMap),
            sortColumns: this.getSortColumns(this._propertyMap),
            entityType: this._dataSourceParams.entityType,
            stateId: this._dataSourceParams.stateId || null,
            assessorId: this._dataSourceParams.assessorId || null,
            collectorId: this._dataSourceParams.collectorId || null,
            // yearBegin: 0,
            // yearEnd: 0,
            onlyPendingQC: this._dataSourceParams.onlyPendingQC
            // includeTotals: false,
            // includeOnlyTotals: false,
            // selectedRows: {
            //     selectAllRows: false,
            //     selectedRows: []
            // },
            // selectedRowProperty: null,
            // overrideFilterColumns: [],
            // advancedAssetFilters: null,
            // range: {
            //     startId: 0,
            //     endId: null
            // }

        };

        return new Promise(async (resolve, reject) => {
            const requestInProgress = this._taxRateCommandCenterService.getList(searchParams).subscribe(result => {
                const data = this._dataSourceParams.entityType == EntityType.TaxAuthority ? result.taxAuthorities : result.taxRateAreas;

                if (this._dataSourceParams.entityType == EntityType.TaxRateArea) {
                    this._taxRateCommandCenterService.allTaxAuthorities = result.taxAuthorities;
                }

                const dataSourceResult: AgGridDataSourceResult = {
                    rows: data,
                    totalRows: result.totalRows,
                    lastModifiedTimestamp: new Date()
                };

                this._canceler = null;
                resolve(dataSourceResult);
            }, () => {
                // Cancel on Error
                this._canceler();
            });

            this._canceler = () => {
                requestInProgress.unsubscribe();
                resolve({ rows: [], totalRows: 0 });
                this._canceler = null;
            };
        });
    }
}
